/**
 * FilterSpec / DisplaySpec ↔ URLSearchParams (11 URL encoding).
 * Key names match contracts exactly.
 */

export type DateFilter = {
  op: 'on' | 'before' | 'after' | 'between';
  value: string;
  value_to?: string;
};

export type FilterSpec = {
  state_ids?: string[] | null;
  state_groups?: string[] | null;
  assignee_ids?: (string | null)[] | null;
  label_ids?: (string | null)[] | null;
  priorities?: string[] | null;
  created_by_ids?: string[] | null;
  parent_ids?: (string | null)[] | null;
  ancestor_id?: string | null;
  start_date?: DateFilter | null;
  due_date?: DateFilter | null;
  query?: string | null;
  types?: string[] | null;
  subtypes?: string[] | null;
};

export type DisplaySpec = {
  layout?: 'list' | 'kanban';
  group_by?: 'state' | null;
  order_by?: string;
  show_empty_groups?: boolean;
  show_sub_items?: boolean;
  /** Omitted means shown; stream lists default it to false. */
  show_bugs?: boolean;
};

export const BACKLOG_DEFAULT_STATE_GROUPS = ['backlog', 'unstarted'] as const;

/** Kanban drops the list's default state-group constraint so every column can appear. */
export function filtersForLayoutSwitch(
  filters: FilterSpec,
  next: 'list' | 'kanban',
  listDefaults: readonly string[],
): FilterSpec {
  if (next !== 'kanban' || filters.state_groups == null) return filters;
  if (
    filters.state_groups.length === listDefaults.length &&
    filters.state_groups.every((group, index) => group === listDefaults[index])
  ) {
    return { ...filters, state_groups: null };
  }
  return filters;
}

/**
 * URL value meaning "no state-group constraint". Same meaning as a missing
 * param. Distinct from an empty CSV (which matches nothing). Never sent to the API.
 */
export const STATE_GROUPS_ANY = '*';

/** Clear all: keep a locked type, drop every other filter, and persist `*`. */
export function clearedWorkListSearchParams(args: {
  lockedType?: string | null;
  display: DisplaySpec;
  peek?: string | null;
}): URLSearchParams {
  const params = filtersToSearchParams(
    args.lockedType ? { types: [args.lockedType] } : {},
    args.display,
  );
  params.set('state_groups', STATE_GROUPS_ANY);
  if (args.peek) params.set('peek', args.peek);
  params.delete('focus');
  return params;
}

const FILTER_KEYS = [
  'state_ids',
  'state_groups',
  'assignee_ids',
  'label_ids',
  'priorities',
  'created_by_ids',
  'parent_ids',
  'start_date',
  'due_date',
  'query',
  'types',
  'subtypes',
] as const;

function csvFromNullableIds(ids: (string | null)[]): string {
  return ids.map((id) => (id === null ? 'null' : id)).join(',');
}

function encodeDate(filter: DateFilter): string {
  if (filter.op === 'between') {
    return `between:${filter.value}:${filter.value_to}`;
  }
  return `${filter.op}:${filter.value}`;
}

function parseDate(raw: string): DateFilter | null {
  const parts = raw.split(':');
  if (parts[0] === 'between' && parts.length === 3) {
    return { op: 'between', value: parts[1], value_to: parts[2] };
  }
  if ((parts[0] === 'on' || parts[0] === 'before' || parts[0] === 'after') && parts.length === 2) {
    return { op: parts[0], value: parts[1] };
  }
  return null;
}

function parseNullableCsv(raw: string): (string | null)[] {
  if (raw === '') return [];
  return raw.split(',').map((part) => {
    const t = part.trim();
    return t === 'null' ? null : t;
  });
}

/** Encode filters + display into URL search params (omit null/undefined). */
export function filtersToSearchParams(
  filters: FilterSpec,
  display: DisplaySpec = {},
  extra: Record<string, string | undefined> = {},
): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.state_ids != null) params.set('state_ids', filters.state_ids.join(','));
  if (filters.state_groups != null) params.set('state_groups', filters.state_groups.join(','));
  if (filters.assignee_ids != null) params.set('assignee_ids', csvFromNullableIds(filters.assignee_ids));
  if (filters.label_ids != null) params.set('label_ids', csvFromNullableIds(filters.label_ids));
  if (filters.priorities != null) params.set('priorities', filters.priorities.join(','));
  if (filters.created_by_ids != null) params.set('created_by_ids', filters.created_by_ids.join(','));
  if (filters.parent_ids != null) params.set('parent_ids', csvFromNullableIds(filters.parent_ids));
  if (filters.ancestor_id != null) params.set('ancestor_id', filters.ancestor_id);
  if (filters.start_date != null) params.set('start_date', encodeDate(filters.start_date));
  if (filters.due_date != null) params.set('due_date', encodeDate(filters.due_date));
  if (filters.query != null && filters.query !== '') params.set('query', filters.query);
  if (filters.types != null) params.set('types', filters.types.join(','));
  if (filters.subtypes != null) params.set('subtypes', filters.subtypes.join(','));

  if (display.layout) params.set('layout', display.layout);
  if (display.order_by) params.set('order_by', display.order_by);
  if (display.group_by === 'state') params.set('group_by', 'state');
  // Always explicit: the API still defaults to showing subtasks when omitted.
  if (display.show_sub_items != null) params.set('show_sub_items', String(display.show_sub_items));
  if (display.show_bugs != null) params.set('show_bugs', String(display.show_bugs));
  if (display.show_empty_groups === false) params.set('show_empty_groups', 'false');

  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== '') params.set(key, value);
  }
  return params;
}

/**
 * Parse URL search params into FilterSpec + DisplaySpec.
 * A missing state_groups param leaves every group visible.
 */
export function searchParamsToFilters(
  search: URLSearchParams,
): { filters: FilterSpec; display: DisplaySpec; peek: string | null } {
  const filters: FilterSpec = {};
  const display: DisplaySpec = {};

  if (search.has('state_ids')) {
    const raw = search.get('state_ids') ?? '';
    filters.state_ids = raw === '' ? [] : raw.split(',').filter(Boolean);
  }
  if (search.has('state_groups')) {
    const raw = search.get('state_groups') ?? '';
    if (raw === STATE_GROUPS_ANY) {
      filters.state_groups = null;
    } else {
      filters.state_groups = raw === '' ? [] : raw.split(',').filter(Boolean);
    }
  }
  if (search.has('assignee_ids')) {
    filters.assignee_ids = parseNullableCsv(search.get('assignee_ids') ?? '');
  }
  if (search.has('label_ids')) {
    filters.label_ids = parseNullableCsv(search.get('label_ids') ?? '');
  }
  if (search.has('priorities')) {
    const raw = search.get('priorities') ?? '';
    filters.priorities = raw === '' ? [] : raw.split(',').filter(Boolean);
  }
  if (search.has('created_by_ids')) {
    const raw = search.get('created_by_ids') ?? '';
    filters.created_by_ids = raw === '' ? [] : raw.split(',').filter(Boolean);
  }
  if (search.has('parent_ids')) {
    filters.parent_ids = parseNullableCsv(search.get('parent_ids') ?? '');
  }
  if (search.has('ancestor_id')) {
    const raw = search.get('ancestor_id') ?? '';
    filters.ancestor_id = raw === '' ? null : raw;
  }
  if (search.has('start_date')) {
    const raw = search.get('start_date');
    filters.start_date = raw ? parseDate(raw) : null;
  }
  if (search.has('due_date')) {
    const raw = search.get('due_date');
    filters.due_date = raw ? parseDate(raw) : null;
  }
  if (search.has('query')) {
    filters.query = search.get('query');
  }
  if (search.has('types')) {
    const raw = search.get('types') ?? '';
    filters.types = raw === '' ? [] : raw.split(',').filter(Boolean);
  }
  if (search.has('subtypes')) {
    const raw = search.get('subtypes') ?? '';
    filters.subtypes = raw === '' ? [] : raw.split(',').filter(Boolean);
  }

  const layout = search.get('layout');
  if (layout === 'kanban' || layout === 'list') display.layout = layout;
  if (search.has('order_by')) display.order_by = search.get('order_by') ?? undefined;
  if (search.has('group_by')) {
    const g = search.get('group_by');
    display.group_by = g === 'state' ? 'state' : null;
  }
  if (search.has('show_sub_items')) {
    display.show_sub_items = search.get('show_sub_items') === 'true';
  }
  if (search.has('show_bugs')) {
    display.show_bugs = search.get('show_bugs') === 'true';
  }
  if (search.has('show_empty_groups')) {
    display.show_empty_groups = search.get('show_empty_groups') !== 'false';
  }

  const peek = search.get('peek');
  return { filters, display, peek: peek && peek.length > 0 ? peek : null };
}

/** True when any FilterSpec key is actively constraining (including empty array). */
export function hasActiveFilters(filters: FilterSpec, search: URLSearchParams): boolean {
  for (const key of FILTER_KEYS) {
    if (search.has(key)) return true;
  }
  return Object.values(filters).some((v) => v !== undefined && v !== null);
}

export function filtersToQueryObject(
  filters: FilterSpec,
  display: DisplaySpec,
): Record<string, string> {
  const params = filtersToSearchParams(filters, display);
  const out: Record<string, string> = {};
  params.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}
